"use client";
import useTranslate from "@app/_hooks/useTranslate";
import Flag from "@components/Flag";
import type { SelectProps } from "@components/ui/Select/Select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/Select/Select";
import { useMarket } from "@context/marketContext";
import { useRouter } from "next/navigation";
import { useState } from "react";
import type { Path } from "react-hook-form";

import {
  getMarketConfigFromCode,
  getMarketConfigsSortedByLabel,
} from "@/config/market-configurations";

import { useCheckoutContext } from "../Checkout.hooks";
import { checkoutStore } from "../Checkout.store";
import type { AddressFormFields } from "../Checkout.types";
import { getCheckoutRedirectUrlByMarket } from "../Checkout.utils";

export default function CheckoutCountrySelect({
  name,
  label,
  ...props
}: SelectProps & {
  name: Path<AddressFormFields>;
  label?: string;
}) {
  const t = useTranslate();
  const router = useRouter();
  const { market } = useMarket().state;
  useCheckoutContext();
  const [open, setOpen] = useState(false);
  const setCheckoutAddress = checkoutStore((state) => state.setAddress);
  const setCheckoutDeliveryAddress = checkoutStore(
    (state) => state.setDeliveryAddress,
  );

  // Sort markets by label in alphabetical order and filter out excluded markets
  const sortedAndFilteredMarkets = getMarketConfigsSortedByLabel();

  async function handleChange(value: string) {
    const selectedMarket = getMarketConfigFromCode(value);

    if (name === "deliveryCountry") {
      setCheckoutDeliveryAddress({
        deliveryCountry: selectedMarket.code,
      });
      return;
    }

    setCheckoutAddress({
      country: selectedMarket.code,
    });

    const redirectUrl = getCheckoutRedirectUrlByMarket(market, selectedMarket);

    router.push(redirectUrl);
  }

  return (
    <Select
      defaultValue={market.loop54Market}
      onValueChange={handleChange}
      onOpenChange={setOpen}
      open={open}
      disabled
      {...props}
    >
      <div className="flex flex-col gap-1 max-md:col-span-2">
        {label && (
          <label htmlFor={name} className="sr-only text-base">
            {label}
          </label>
        )}
        <SelectTrigger id={name} open={open}>
          <SelectValue placeholder={t("selectCountry")} />
        </SelectTrigger>
      </div>
      <SelectContent>
        {sortedAndFilteredMarkets.map((market) => {
          return (
            <SelectItem key={market.code} value={market.code}>
              <span className="flex grow content-between items-center gap-2">
                <Flag
                  code={market.code}
                  className="h-4 w-4 flex-shrink-0 rounded-full border border-black object-cover"
                />
                <span className="flex items-center gap-1">
                  {market.label}
                  <span className="text-base uppercase text-grey-200">
                    ({market.currency})
                  </span>
                </span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
